import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertTriangle, CalendarClock, Copy, FileAudio, Mail, MessageCircle, RefreshCw, Send, Upload, UserRoundCog, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { cn } from '@/lib/cn';
import MeetingDetail from '@/components/meetings/MeetingDetail';
import RecordingCard, { RECORDER_LIVE } from '@/components/meetings/RecordingCard';
import { bidi, BIDI_CLS } from '@/lib/bidi';
import {
  cancelMeeting, dubai, fetchCallCandidates, fetchMeeting, fetchMeetingReview, fileMeeting, finishCallAudio, handoverMeeting,
  MEETING_STATUS, newMeetingLink, putToSession, resendDelivery, startCallAudio, whatsappText,
  type FileItem, type Meeting,
} from '@/lib/calls';

const errMsg = (e: unknown) => (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Something went wrong';

/** Phone numbers, emails and long digit runs: flagged in any note before it is filed,
 *  because notes travel to the crew, the editor and (the clinic note) to the portal. */
const SENSITIVE = /(\+?\d[\d\s-]{7,}\d)|([^\s@]+@[^\s@]+\.[^\s@]+)|(\b\d{6,}\b)/;

function clock(sec: number) {
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function MeetingPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['meeting', id], queryFn: () => fetchMeeting(id), refetchInterval: (qq) => {
    const d = qq.state.data;
    if (d && (RECORDER_LIVE.includes(d.recorder_state ?? '') || d.transcript_pending)) return 15_000;
    const st = d?.status;
    return st === 'transcribing' || st === 'booking' ? 10_000 : false;
  } });
  const m = q.data;
  const refresh = (next?: Meeting) => {
    if (next) qc.setQueryData(['meeting', id], next);
    qc.invalidateQueries({ queryKey: ['meetings'] });
    qc.invalidateQueries({ queryKey: ['meeting-review', id] });
  };

  if (q.isLoading) return <div className="text-sm text-muted">Loading call...</div>;
  if (!m) return <div className="text-sm text-muted">This call could not be found, or it is not yours to see.</div>;
  if (m.purpose === 'meeting') return <MeetingDetail m={m} onChange={refresh} />;
  const st = MEETING_STATUS[m.status];

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <Link to="/creatives" className="text-xs text-muted hover:text-muted-strong">Back to creatives</Link>
        <div className="flex flex-wrap items-center gap-3 mt-1">
          <h1 className="text-xl font-semibold text-muted-strong">{m.title}</h1>
          <span className={cn('px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide', st.cls)} data-testid="meeting-status">{st.label}</span>
          {m.dry_run && <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wide bg-amber-500/15 text-amber-300">dry run</span>}
        </div>
        <p className="text-xs text-muted mt-1">
          Run by {m.organizer_name}{m.starts_at && ` · ${dubai(m.starts_at)} Dubai time`}
          {m.meet_uri && <> · <a href={m.meet_uri} target="_blank" rel="noreferrer" className="text-accent-400 hover:underline">Google Meet</a></>}
        </p>
        {m.last_error && <p className="text-xs text-red-300 mt-1">{m.last_error}</p>}
      </div>

      <Scripts m={m} />
      <RecordingCard m={m} showTranscript={false} />
      {m.status === 'review' && m.can_manage && <Confirm m={m} onFiled={refresh} />}
      {['booked', 'awaiting_audio', 'failed'].includes(m.status) && m.can_manage && <AudioUpload m={m} onDone={refresh} />}
      {m.status === 'transcribing' && (
        <Card><div className="flex items-center gap-2 text-sm text-muted"><RefreshCw size={14} className="animate-spin" />
          Transcribing on KAF's server. A 30 minute call takes about half an hour; you are told when it is ready.</div></Card>
      )}
      <Invitations m={m} onChange={refresh} />
      {m.can_manage && <Manage m={m} onChange={refresh} />}
    </div>
  );
}

function Scripts({ m }: { m: Meeting }) {
  const OUT: Record<string, string> = { approved: 'Approved on the call', changes_requested: 'Changes asked on the call', not_discussed: 'Not discussed' };
  return (
    <Card>
      <div className="text-[10px] uppercase tracking-wide text-muted mb-2">Scripts on this call</div>
      <div className="divide-y divide-slate-800">
        {m.creatives.map((c) => (
          <div key={c.creative_id} className="py-2 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/creatives/${c.creative_id}`} className="font-mono text-[11px] text-accent-400 hover:underline">{c.code}</Link>
              <span className="text-muted-strong">{c.title}</span>
              {c.talent_name && <span className="text-xs text-muted">{c.talent_name}</span>}
              <span className="ml-auto text-xs text-muted">
                {c.final_outcome ? OUT[c.final_outcome] : c.decided_outside ? 'Decided outside the call' : c.script_status.replace('_', ' ')}
              </span>
            </div>
            {c.changed_since_booking && !c.filed_at && <div className="text-[11px] text-amber-300 mt-0.5">The script changed after the link was sent.</div>}
            {c.clinic_note && <div className="text-xs text-muted mt-1"><span className="text-muted-strong">To the clinic:</span> {c.clinic_note}</div>}
            {c.staff_note && <div className="text-xs text-muted mt-0.5 whitespace-pre-wrap"><span className="text-muted-strong">For the team:</span> {c.staff_note}</div>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function Invitations({ m, onChange }: { m: Meeting; onChange: (x?: Meeting) => void }) {
  const resend = useMutation({ mutationFn: (rowId: string) => resendDelivery(m.id, rowId), onSuccess: (x) => { toast.success('Sent again'); onChange(x); }, onError: (e) => toast.error(errMsg(e)) });
  const copy = async (text: string, what: string) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); } catch { toast.error('Copy did not work; select the text instead'); }
  };
  return (
    <Card>
      <div className="text-[10px] uppercase tracking-wide text-muted mb-2">Invited</div>
      <div className="space-y-1">
        {m.invitees.map((i) => <div key={i.email} className="text-sm"><span className="text-muted-strong">{i.name}</span> <span className="text-xs text-muted">{i.email}</span></div>)}
      </div>
      {m.suggestion && (
        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
          None of the times suit them. They suggested: <span className="text-muted-strong">{m.suggestion}</span>
        </div>
      )}
      <div className="text-[10px] uppercase tracking-wide text-muted mt-4 mb-2">Messages</div>
      <div className="space-y-1">
        {m.deliveries.map((d) => (
          <div key={d.id} className="flex items-center gap-2 text-xs">
            {d.channel === 'telegram' ? <MessageCircle size={12} className="text-muted" /> : <Mail size={12} className="text-muted" />}
            <span className="text-muted-strong truncate">{d.channel === 'telegram' ? 'Telegram' : d.recipient}</span>
            <span className="text-muted">{d.kind.startsWith('reminder_1h') ? '1 hour reminder' : d.kind.startsWith('reminder_24h') ? '24 hour reminder' : 'booking link'}</span>
            <span className={cn('ml-auto', d.status === 'sent' ? 'text-emerald-300' : d.status === 'failed' ? 'text-red-300' : 'text-amber-300')}
              title={d.error ?? ''}>{d.error === 'dry-run: not sent' ? 'dry run' : d.status === 'unknown' ? 'may not have arrived' : d.status}</span>
            {m.can_manage && (d.status === 'failed' || d.status === 'unknown') && (
              <button type="button" className="text-accent-400 hover:underline" onClick={() => resend.mutate(d.id)}>Send again</button>
            )}
          </div>
        ))}
      </div>
      {m.link && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" icon={<Copy size={12} />} onClick={() => copy(m.link!, 'Link')}>Copy link</Button>
          <Button size="sm" variant="secondary" icon={<MessageCircle size={12} />}
            onClick={() => copy(whatsappText(m.invitees.find((i) => i.kind !== 'staff')?.name ?? '', m.creatives.length, m.link!), 'WhatsApp message')}>Copy WhatsApp message</Button>
          {m.link_opened_at ? <span className="text-xs text-muted">Opened {dubai(m.link_opened_at)}</span> : <span className="text-xs text-muted">Not opened yet</span>}
        </div>
      )}
    </Card>
  );
}

function Manage({ m, onChange }: { m: Meeting; onChange: (x?: Meeting) => void }) {
  const [cancelling, setCancelling] = useState(false);
  const [to, setTo] = useState('');
  const open = ['link_sent', 'booking', 'booked', 'awaiting_audio', 'transcribing', 'review'].includes(m.status);
  const firstScript = m.creatives[0]?.creative_id ?? '';
  const people = useQuery({ queryKey: ['call-candidates', firstScript], queryFn: () => fetchCallCandidates(firstScript), enabled: open && !!firstScript });
  const hand = useMutation({ mutationFn: () => handoverMeeting(m.id, to), onSuccess: (x) => { toast.success('Handed over'); setTo(''); onChange(x); }, onError: (e) => toast.error(errMsg(e)) });
  const cancel = useMutation({ mutationFn: () => cancelMeeting(m.id), onSuccess: (x) => { toast.success('Call cancelled'); setCancelling(false); onChange(x); }, onError: (e) => toast.error(errMsg(e)) });
  const relink = useMutation({ mutationFn: () => newMeetingLink(m.id), onSuccess: (x) => { toast.success('A new link was sent'); onChange(x); }, onError: (e) => toast.error(errMsg(e)) });
  if (!open && m.status !== 'expired') return null;
  const ended = !!m.ends_at && new Date(m.ends_at) < new Date();
  return (
    <Card>
      <div className="text-[10px] uppercase tracking-wide text-muted mb-3">Change the call</div>
      <div className="flex flex-wrap items-end gap-3">
        {open && !ended && (
          <div className="flex items-end gap-2 w-full sm:w-auto">
            <div className="w-56"><Select label="Hand over to" value={to} onChange={setTo} searchable
              options={(people.data?.organizers ?? []).filter((o) => o.id !== m.organizer_id).map((o) => ({ value: o.id, label: o.name }))} /></div>
            <Button size="sm" variant="secondary" icon={<UserRoundCog size={12} />} disabled={!to} loading={hand.isPending} onClick={() => hand.mutate()}>Hand over</Button>
          </div>
        )}
        {(m.status === 'link_sent' || m.status === 'expired') && (
          <Button size="sm" variant="secondary" icon={<Send size={12} />} loading={relink.isPending} onClick={() => relink.mutate()}>Send a new link</Button>
        )}
        {open && <Button size="sm" variant="danger" icon={<X size={12} />} onClick={() => setCancelling(true)}>Cancel the call</Button>}
      </div>
      <ConfirmDialog open={cancelling} title="Cancel this call?" tone="danger" confirmLabel="Cancel the call"
        message="Everyone invited is told. The scripts on it wait for the doctor again, with a fresh clock."
        onConfirm={() => cancel.mutate()} onCancel={() => setCancelling(false)} />
    </Card>
  );
}

function AudioUpload({ m, onDone }: { m: Meeting; onDone: (x?: Meeting) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [pct, setPct] = useState<number | null>(null);
  async function go(file: File) {
    try {
      setPct(0);
      const { upload_url, object } = await startCallAudio(m.id, file);
      await putToSession(upload_url, file, setPct);
      const next = await finishCallAudio(m.id, object);
      toast.success('Uploaded. Transcription has started.');
      onDone(next);
    } catch (e) {
      toast.error(e instanceof Error && !(e as { response?: unknown }).response ? e.message : errMsg(e));
    } finally {
      setPct(null);
    }
  }
  const notYet = m.status === 'booked' && m.starts_at && new Date(m.starts_at) > new Date();
  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3">
        <FileAudio size={16} className="text-accent-400" />
        <div className="flex-1 min-w-0">
          <div className="text-sm text-muted-strong">The call's audio</div>
          <div className="text-xs text-muted">Any recording works: the Meet recording from Drive, or a phone voice memo. Up to 90 minutes. It is transcribed on KAF's own server and the file is deleted afterwards.</div>
        </div>
        <input ref={input} type="file" accept="audio/*,video/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) go(f); e.currentTarget.value = ''; }} />
        <Button size="sm" icon={<Upload size={12} />} disabled={pct !== null || !!notYet} onClick={() => input.current?.click()} data-testid="upload-audio">
          {pct !== null ? `Uploading ${pct}%` : m.status === 'failed' ? 'Upload again' : 'Upload the audio'}
        </Button>
      </div>
      {notYet && <p className="text-xs text-muted mt-2">You can upload once the call has started.</p>}
    </Card>
  );
}

type Choice = { outcome?: FileItem['outcome']; decided_by_name: string; staff_note: string; clinic_note: string; override: boolean };

function Confirm({ m, onFiled }: { m: Meeting; onFiled: (x?: Meeting) => void }) {
  const r = useQuery({ queryKey: ['meeting-review', m.id], queryFn: () => fetchMeetingReview(m.id) });
  const rv = r.data;
  const [choice, setChoice] = useState<Record<string, Choice>>({});
  useEffect(() => {
    if (!rv) return;
    const next: Record<string, Choice> = {};
    for (const s of rv.scripts) {
      const quotes = s.quotes.map((i) => rv.transcript[i]).filter(Boolean).map((t) => `[${clock(t.start)}] ${t.text}`).join('\n');
      next[s.creative_id] = { decided_by_name: s.talent_name ?? '', staff_note: quotes, clinic_note: '', override: false };
    }
    setChoice(next);
  }, [rv]);

  const file = useMutation({
    mutationFn: () => fileMeeting(m.id, (rv?.scripts ?? []).filter((s) => !s.filed_at && !s.decided_outside).map((s) => {
      const c = choice[s.creative_id];
      return { creative_id: s.creative_id, outcome: c.outcome!, decided_by_name: c.decided_by_name || undefined,
               staff_note: c.staff_note || undefined, clinic_note: c.outcome === 'not_discussed' ? undefined : (c.clinic_note || undefined),
               override: c.override };
    })),
    onSuccess: (x) => { toast.success('Filed. Each script has its outcome.'); onFiled(x); },
    onError: (e) => toast.error(errMsg(e)),
  });

  if (r.isLoading) return <Card><div className="text-sm text-muted">Loading the transcript...</div></Card>;
  if (!rv) return <Card><div className="text-sm text-muted">The transcript is not available to you.</div></Card>;

  const open = rv.scripts.filter((s) => !s.filed_at && !s.decided_outside);
  const ready = open.every((s) => {
    const c = choice[s.creative_id];
    return c?.outcome && (!s.changed_since_booking || c.override || c.outcome === 'not_discussed');
  });
  const set = (id: string, patch: Partial<Choice>) => setChoice((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  return (
    <Card>
      <div className="flex items-center gap-2 mb-1"><CalendarClock size={14} className="text-accent-400" />
        <h2 className="text-sm font-semibold text-muted-strong">Confirm each script</h2></div>
      <p className="text-xs text-muted mb-4">Nothing is chosen for you. Read the lines under each script, pick what the doctor decided, and write the note the clinic will see.
        {rv.minutes ? ` ${Math.round(rv.minutes)} minute call.` : ''}</p>
      <div className="space-y-5">
        {open.map((s) => {
          const c = choice[s.creative_id] ?? { decided_by_name: '', staff_note: '', clinic_note: '', override: false };
          const flagged = SENSITIVE.test(c.staff_note) || SENSITIVE.test(c.clinic_note);
          return (
            <div key={s.creative_id} className="rounded-lg border border-slate-800 p-3" data-testid="confirm-script">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-[11px] text-muted">{s.code}</span>
                <span className="text-sm text-muted-strong">{s.title}</span>
                {s.talent_name && <span className="text-xs text-muted">{s.talent_name}</span>}
              </div>
              {s.changed_since_booking && (
                <label className="mt-2 flex items-start gap-2 text-xs text-amber-300">
                  <input type="checkbox" className="accent-amber-400 mt-0.5" checked={c.override} onChange={(e) => set(s.creative_id, { override: e.target.checked })} />
                  <span><AlertTriangle size={11} className="inline mr-1" />The script was edited after the link went out. Tick to confirm the doctor's decision applies to the current text.</span>
                </label>
              )}
              <div className="mt-2 max-h-40 overflow-y-auto rounded bg-bg px-2 py-1.5 text-xs text-muted space-y-1">
                {s.quotes.length ? s.quotes.map((i) => rv.transcript[i] && (
                  <div key={i}><span className="font-mono text-[10px] text-muted/70 mr-1.5">{clock(rv.transcript[i].start)}</span>{rv.transcript[i].text}</div>
                )) : <div className="italic">No lines matched this script. Check the full transcript below.</div>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3" role="radiogroup" aria-label={`Outcome for ${s.code}`}>
                {([['approved', 'Approved'], ['changes_requested', 'Wants changes'], ['not_discussed', 'Not discussed']] as const).map(([v, label]) => (
                  <button key={v} type="button" role="radio" aria-checked={c.outcome === v} onClick={() => set(s.creative_id, { outcome: v })}
                    className={cn('rounded-full px-3 py-1.5 text-xs border min-h-[36px]', c.outcome === v
                      ? v === 'approved' ? 'border-emerald-400 bg-emerald-500/15 text-emerald-200' : v === 'changes_requested' ? 'border-red-400 bg-red-500/15 text-red-200' : 'border-slate-500 bg-slate-700/40 text-slate-200'
                      : 'border-slate-700 text-muted hover:text-muted-strong')}>{label}</button>
                ))}
              </div>
              {c.outcome && c.outcome !== 'not_discussed' && (
                <div className="grid gap-2 mt-3">
                  <label className="text-xs text-muted">Decided by
                    <input value={c.decided_by_name} onChange={(e) => set(s.creative_id, { decided_by_name: e.target.value })}
                      className="mt-1 block w-full h-9 px-3 rounded-lg bg-bg border border-slate-800 text-sm text-muted-strong" />
                  </label>
                  <label className="text-xs text-muted">Note to the clinic (shown in their portal)
                    <textarea value={c.clinic_note} onChange={(e) => set(s.creative_id, { clinic_note: e.target.value })} rows={2} maxLength={2000}
                      placeholder={c.outcome === 'approved' ? 'Approved as written.' : 'What they asked to change, in plain words.'}
                      className="mt-1 block w-full rounded-lg bg-bg border border-slate-800 px-3 py-2 text-sm text-muted-strong" />
                  </label>
                </div>
              )}
              {c.outcome && (
                <label className="block text-xs text-muted mt-2">Note for the team (stays inside KAF)
                  <textarea value={c.staff_note} onChange={(e) => set(s.creative_id, { staff_note: e.target.value })} rows={3} maxLength={4000}
                    className="mt-1 block w-full rounded-lg bg-bg border border-slate-800 px-3 py-2 text-xs text-muted-strong" />
                </label>
              )}
              {flagged && <p className="text-[11px] text-amber-300 mt-1">A note contains a phone number, an email or a long number. Remove any patient detail before filing.</p>}
            </div>
          );
        })}
      </div>
      <details className="mt-4">
        <summary className="text-xs text-muted cursor-pointer">Full transcript ({rv.transcript.length} lines)</summary>
        <div className="mt-2 max-h-80 overflow-y-auto rounded bg-bg p-2 text-xs text-muted space-y-1" dir="auto">
          {rv.transcript.map((t, i) => <div key={i} dir="auto" className={BIDI_CLS}><span className="font-mono text-[10px] text-muted/70 mr-1.5">{clock(t.start)}</span>{bidi(t.text)}</div>)}
        </div>
      </details>
      <div className="flex justify-end mt-4">
        <Button icon={<Send size={14} />} disabled={!ready} loading={file.isPending} onClick={() => file.mutate()} data-testid="file-call"
          title={ready ? '' : 'Choose an outcome for every script'}>File {open.length} outcome{open.length === 1 ? '' : 's'}</Button>
      </div>
    </Card>
  );
}

